import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';

const BASE = '/documentation/user_guides';

export const fetchPublishedGuides = createAsyncThunk(
  'userGuide/fetchPublished',
  async (q = '', { rejectWithValue }) => {
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const res = await apiAxios.get(`${BASE}${params}`);
      return res.data?.data || [];
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const fetchUserGuides = createAsyncThunk(
  'userGuide/fetchAll',
  async (q = '', { rejectWithValue }) => {
    try {
      const params = q ? `?all=true&q=${encodeURIComponent(q)}` : '?all=true';
      const res = await apiAxios.get(`${BASE}${params}`);
      return res.data?.data || [];
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const createUserGuide = createAsyncThunk(
  'userGuide/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(BASE, { user_guide: payload });
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const updateUserGuide = createAsyncThunk(
  'userGuide/update',
  async ({ slug, payload }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.patch(`${BASE}/${slug}`, { user_guide: payload });
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const deleteUserGuide = createAsyncThunk(
  'userGuide/delete',
  async (slug, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${BASE}/${slug}`);
      return slug;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// Uploads one image to S3 and returns its durable URL. Plain helper (not a
// thunk) so the RichTextEditor's onImageUpload(file) => Promise<url> can call it.
export const uploadUserGuideImage = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const res = await apiAxios.post(`${BASE}/upload_image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data?.data?.url;
};

const upsert = (list, updated) =>
  list.map(g => g.slug === updated.slug ? updated : g);

const userGuideSlice = createSlice({
  name: 'userGuide',
  initialState: { data: [], loading: false, saving: false, error: null },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPublishedGuides.pending,  s => { s.loading = true;  s.error = null; })
      .addCase(fetchPublishedGuides.fulfilled, (s, a) => { s.loading = false; s.data = a.payload; })
      .addCase(fetchPublishedGuides.rejected,  (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(fetchUserGuides.pending,   s => { s.loading = true;  s.error = null; })
      .addCase(fetchUserGuides.fulfilled, (s, a) => { s.loading = false; s.data = a.payload; })
      .addCase(fetchUserGuides.rejected,  (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(createUserGuide.pending,   s => { s.saving = true;  s.error = null; })
      .addCase(createUserGuide.fulfilled, (s, a) => { s.saving = false; s.data = [a.payload, ...s.data]; })
      .addCase(createUserGuide.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

      .addCase(updateUserGuide.pending,   s => { s.saving = true;  s.error = null; })
      .addCase(updateUserGuide.fulfilled, (s, a) => { s.saving = false; s.data = upsert(s.data, a.payload); })
      .addCase(updateUserGuide.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

      .addCase(deleteUserGuide.fulfilled, (s, a) => {
        s.data = s.data.filter(g => g.slug !== a.payload);
      });
  }
});

export default userGuideSlice.reducer;
